import { useState } from 'react';
import { t, plural } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { useBookmarkSelection } from '../../contexts/BookmarkSelectionContext';
import { useBookmarkPageContent } from '../../contexts/BookmarkPageContext';
import { useBatchActions } from '../../hooks/useBatchActions';
import { useBatchOperationTarget } from '../../hooks/useBatchOperationTarget';
import BatchTagPopover from './BatchTagPopover';
import BatchMovePopover from './BatchMovePopover';
import ButtonIcon from '../ui/ButtonIcon';
import Popover from '../ui/Popover';
import type { ViewMode } from '../../types/api';
import type { PageInfo } from '../../utils/pageInfo';
import type { BookmarkListBatchMeta } from './types';

interface BookmarkBatchOperationsProps {
  viewMode?: ViewMode;
  pageInfo?: PageInfo | null;
  bookmarkListBatchMeta?: BookmarkListBatchMeta | null;
}

function BookmarkBatchOperations({
  viewMode = 'unread',
  pageInfo = null,
  bookmarkListBatchMeta = null,
}: BookmarkBatchOperationsProps) {
  const { clearSelection, disableBatchMode, selectAll } = useBookmarkSelection();
  const {
    batchArchive,
    batchDelete,
    batchUnarchive,
    batchSectionArchive,
    batchSectionDelete,
    batchSectionUnarchive,
  } = useBatchActions();
  const bookmarkPage = useBookmarkPageContent();
  const { i18n } = useLingui();
  const [showTagPopover, setShowTagPopover] = useState(false);
  const [showMovePopover, setShowMovePopover] = useState(false);

  const {
    isSectionBatch,
    section,
    batchTarget,
    canRunBatch,
    effectiveCount,
    bookmarkIds,
  } = useBatchOperationTarget({
    viewMode,
    pageInfo,
    bookmarkListBatchMeta,
  });

  const allBookmarkIds = bookmarkPage?.bookmarks.map((bookmark) => bookmark.id) || [];
  const allSelected =
    isSectionBatch ||
    (allBookmarkIds.length > 0 &&
      allBookmarkIds.every((id) => bookmarkIds.includes(String(id))));
  const someSelected = bookmarkIds.length > 0 && !allSelected;

  const isArchiveView = viewMode === 'archive';
  const showCountLabel = canRunBatch;
  const countDenominator =
    typeof pageInfo?.count === 'number' && isSectionBatch
      ? pageInfo.count
      : allBookmarkIds.length;

  const handleBatchArchive = async () => {
    if (!canRunBatch) return;

    if (isSectionBatch && section) {
      if (isArchiveView) {
        await batchSectionUnarchive.mutateAsync(section);
      } else {
        await batchSectionArchive.mutateAsync(section);
      }
    } else if (isArchiveView) {
      await batchUnarchive.mutateAsync(bookmarkIds);
    } else {
      await batchArchive.mutateAsync(bookmarkIds);
    }
    disableBatchMode();
  };

  const handleBatchDelete = async () => {
    if (!canRunBatch) return;

    if (
      window.confirm(
        plural(effectiveCount, {
          one: 'Are you sure you want to delete # article?',
          other: 'Are you sure you want to delete # articles?',
        }),
      )
    ) {
      if (isSectionBatch && section) {
        await batchSectionDelete.mutateAsync(section);
      } else {
        await batchDelete.mutateAsync(bookmarkIds);
      }
      disableBatchMode();
    }
  };

  const handleSelectAll = () => {
    if (allSelected) {
      clearSelection();
    } else {
      selectAll(allBookmarkIds);
    }
  };

  const archivePending = isArchiveView
    ? isSectionBatch
      ? batchSectionUnarchive.isPending
      : batchUnarchive.isPending
    : isSectionBatch
      ? batchSectionArchive.isPending
      : batchArchive.isPending;

  const deletePending = isSectionBatch
    ? batchSectionDelete.isPending
    : batchDelete.isPending;

  return (
    <div className="flex w-full items-center space-x-4">
      <div className="batch-operations-slide-in flex items-center space-x-1">
        {allBookmarkIds.length > 0 && (
          <button
            onClick={handleSelectAll}
            className="flex items-center pr-3"
            title={allSelected ? t`Deselect all` : t`Select all`}
          >
            <div className="checkbox-button">
              {allSelected ? (
                <svg
                  width="7"
                  height="6"
                  viewBox="0 0 9 8"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 4.31034L3.69231 7L8 1"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : someSelected ? (
                <svg
                  width="6"
                  height="1"
                  viewBox="0 0 6 1"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <line y1="0.5" x2="6" y2="0.5" stroke="currentColor" strokeWidth="1" />
                </svg>
              ) : null}
            </div>
          </button>
        )}

        <Popover
          open={showTagPopover}
          onOpenChange={setShowTagPopover}
          placement="bottom"
          preventCloseOnContentClick={true}
          content={
            <BatchTagPopover
              batchTarget={batchTarget}
              onClose={() => setShowTagPopover(false)}
            />
          }
        >
          <ButtonIcon
            icon="tag"
            onClick={() => setShowTagPopover(true)}
            disabled={!canRunBatch}
            title={t`Tag`}
            className="disabled:opacity-50"
          />
        </Popover>
        <Popover
          open={showMovePopover}
          onOpenChange={setShowMovePopover}
          placement="bottom"
          preventCloseOnContentClick={true}
          content={
            <BatchMovePopover
              batchTarget={batchTarget}
              onClose={() => setShowMovePopover(false)}
            />
          }
        >
          <ButtonIcon
            icon="folder"
            onClick={() => setShowMovePopover(true)}
            disabled={!canRunBatch}
            title={t`Move`}
            className="disabled:opacity-50"
          />
        </Popover>

        <ButtonIcon
          icon={isArchiveView ? 'archive-filled' : 'archive'}
          onClick={handleBatchArchive}
          disabled={!canRunBatch || archivePending}
          title={
            isArchiveView
              ? archivePending
                ? t`Unarchiving...`
                : t`Unarchive`
              : archivePending
                ? t`Archiving...`
                : t`Archive`
          }
          className="disabled:opacity-50"
        />

        <ButtonIcon
          icon="trash"
          onClick={handleBatchDelete}
          disabled={!canRunBatch || deletePending}
          title={deletePending ? t`Deleting...` : t`Delete`}
          className="text-danger hover:text-danger-hover disabled:opacity-50"
        />
      </div>
      {showCountLabel && (
        <span className="text-copy-tertiary text-xs font-medium select-none">
          {i18n.number(effectiveCount)}
          {countDenominator > 0 ? ` / ${i18n.number(countDenominator)}` : ''}
        </span>
      )}
    </div>
  );
}

export default BookmarkBatchOperations;
